// src/components/ChecklistBuilder/index.tsx
import React, { useState } from 'react';
import styles from './ChecklistBuilder.module.css';
import { notify } from '../../stores/useNotificationStore';

interface Requisito {
  id?: number;
  nome_requisito: string;
  descricao: string;
  peso_percentual: number;
  ordem: number;
  itens: ItemRequisito[];
}

interface ItemRequisito {
  id?: number;
  descricao_item: string;
  obrigatorio: boolean;
  ordem: number;
}

interface ChecklistBuilderProps {
  requisitos: Requisito[];
  onRequisitosChange: (requisitos: Requisito[]) => void;
}

const ChecklistBuilder: React.FC<ChecklistBuilderProps> = ({ requisitos, onRequisitosChange }) => {
  const [newRequisito, setNewRequisito] = useState({
    nome_requisito: '',
    descricao: '',
    peso_percentual: 25,
    ordem: 0
  });

  const [newItem, setNewItem] = useState({
    descricao_item: '',
    obrigatorio: true,
    ordem: 0
  });

  const [selectedRequisitoIndex, setSelectedRequisitoIndex] = useState<number | null>(null);

  const handleAddRequisito = () => {
    if (!newRequisito.nome_requisito.trim()) {
      notify.warning('Por favor, informe o nome do requisito.');
      return;
    }

    const requisito: Requisito = {
      nome_requisito: newRequisito.nome_requisito,
      descricao: newRequisito.descricao,
      peso_percentual: Number(newRequisito.peso_percentual),
      ordem: requisitos.length,
      itens: []
    };

    onRequisitosChange([...requisitos, requisito]);
    setNewRequisito({ nome_requisito: '', descricao: '', peso_percentual: 25, ordem: 0 });
  };

  const handleAddItem = (requisitoIndex: number) => {
    if (!newItem.descricao_item.trim()) {
      notify.warning('Por favor, informe a descrição do item.');
      return;
    }

    const updatedRequisitos = [...requisitos];
    const requisito = { ...updatedRequisitos[requisitoIndex] };
    const newItemObj: ItemRequisito = {
      descricao_item: newItem.descricao_item,
      obrigatorio: newItem.obrigatorio,
      ordem: requisito.itens.length
    };

    requisito.itens = [...requisito.itens, newItemObj];
    updatedRequisitos[requisitoIndex] = requisito;

    onRequisitosChange(updatedRequisitos);
    setNewItem({ descricao_item: '', obrigatorio: true, ordem: 0 });
  };

  const handleRemoveRequisito = (index: number) => {
    const updatedRequisitos = requisitos.filter((_, i) => i !== index);
    onRequisitosChange(updatedRequisitos);
    
    if (selectedRequisitoIndex === index) {
      setSelectedRequisitoIndex(null);
    }
  };

  const handleRemoveItem = (requisitoIndex: number, itemIndex: number) => {
    const updatedRequisitos = [...requisitos];
    const requisito = { ...updatedRequisitos[requisitoIndex] };
    requisito.itens = requisito.itens.filter((_, i) => i !== itemIndex);
    updatedRequisitos[requisitoIndex] = requisito;
    
    onRequisitosChange(updatedRequisitos);
  };

  const handleToggleItem = (requisitoIndex: number, itemIndex: number) => {
    const updatedRequisitos = [...requisitos];
    const requisito = { ...updatedRequisitos[requisitoIndex] };
    const item = { ...requisito.itens[itemIndex] };
    item.obrigatorio = !item.obrigatorio;
    
    requisito.itens[itemIndex] = item;
    updatedRequisitos[requisitoIndex] = requisito;
    
    onRequisitosChange(updatedRequisitos);
  };

  const handleUpdateItem = (requisitoIndex: number, itemIndex: number, field: keyof ItemRequisito, value: any) => {
    const updatedRequisitos = [...requisitos];
    const requisito = { ...updatedRequisitos[requisitoIndex] };
    const item = { ...requisito.itens[itemIndex] };
    
    if (field === 'descricao_item') {
      item.descricao_item = value;
    } else if (field === 'obrigatorio') {
      item.obrigatorio = value;
    } else if (field === 'ordem') {
      item.ordem = value;
    }
    
    requisito.itens[itemIndex] = item;
    updatedRequisitos[requisitoIndex] = requisito;
    
    onRequisitosChange(updatedRequisitos);
  };

  return (
    <div className={styles.checklistBuilder}>
      <h4>Construtor de Checklist</h4>
      
      {/* Formulário para adicionar novo requisito */}
      <div className={styles.addRequisitoForm}>
        <h5>Adicionar Requisito</h5>
        <input
          type="text"
          placeholder="Nome do requisito"
          value={newRequisito.nome_requisito}
          onChange={(e) => setNewRequisito({ ...newRequisito, nome_requisito: e.target.value })}
          className={styles.inputField}
        />
        <input
          type="text"
          placeholder="Descrição do requisito"
          value={newRequisito.descricao}
          onChange={(e) => setNewRequisito({ ...newRequisito, descricao: e.target.value })}
          className={styles.inputField}
        />
        <input
          type="number"
          placeholder="Peso percentual"
          value={newRequisito.peso_percentual}
          onChange={(e) => setNewRequisito({ ...newRequisito, peso_percentual: Number(e.target.value) })}
          className={styles.inputField}
          min="0"
          max="100"
          step="1"
        />
        <button onClick={handleAddRequisito} className={styles.addButton}>
          Adicionar Requisito
        </button>
      </div>

      {/* Lista de requisitos */}
      <div className={styles.requisitosList}>
        {requisitos.length === 0 ? (
          <p className={styles.emptyMessage}>Nenhum requisito adicionado ainda.</p>
        ) : (
          requisitos.map((requisito, index) => (
            <div key={index} className={styles.requisitoCard}>
              <div className={styles.requisitoHeader}>
                <h6>{requisito.nome_requisito || `Requisito ${index + 1}`}</h6>
                <div className={styles.requisitoActions}>
                  <button
                    onClick={() => setSelectedRequisitoIndex(
                      selectedRequisitoIndex === index ? null : index
                    )}
                    className={styles.toggleButton}
                  >
                    {selectedRequisitoIndex === index ? 'Ocultar Itens' : 'Mostrar Itens'}
                  </button>
                  <button
                    onClick={() => handleRemoveRequisito(index)}
                    className={styles.removeButton}
                    title="Remover requisito"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              {requisito.descricao && (
                <p className={styles.requisitoDescricao}>{requisito.descricao}</p>
              )}
              
              <div className={styles.requisitoDetails}>
                <span className={styles.pesoBadge}>
                  Peso: {requisito.peso_percentual}%
                </span>
              </div>

              {/* Formulário para adicionar item ao requisito */}
              {selectedRequisitoIndex === index && (
                <div className={styles.addItemForm}>
                  <input
                    type="text"
                    placeholder="Descrição do item"
                    value={newItem.descricao_item}
                    onChange={(e) => setNewItem({ ...newItem, descricao_item: e.target.value })}
                    className={styles.inputField}
                  />
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newItem.obrigatorio}
                      onChange={(e) => setNewItem({ ...newItem, obrigatorio: e.target.checked })}
                    />
                    Obrigatório
                  </label>
                  <button
                    onClick={() => handleAddItem(index)}
                    className={styles.addButton}
                  >
                    Adicionar Item
                  </button>
                </div>
              )}

              {/* Lista de itens do requisito */}
              {selectedRequisitoIndex === index && (
                <div className={styles.itemsList}>
                  {requisito.itens.length === 0 ? (
                    <p className={styles.emptyMessage}>Nenhum item adicionado a este requisito.</p>
                  ) : (
                    requisito.itens.map((item, itemIndex) => (
                      <div key={itemIndex} className={styles.itemRow}>
                        <input
                          type="checkbox"
                          checked={item.obrigatorio}
                          onChange={() => handleToggleItem(index, itemIndex)}
                          className={styles.checkbox}
                        />
                        <input
                          type="text"
                          value={item.descricao_item}
                          onChange={(e) => handleUpdateItem(index, itemIndex, 'descricao_item', e.target.value)}
                          className={styles.itemDescription}
                        />
                        <button
                          onClick={() => handleRemoveItem(index, itemIndex)}
                          className={styles.removeButton}
                          title="Remover item"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChecklistBuilder;